import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Paragraph, Table } from '@digdir/designsystemet-react';
import type { WorkEnvironmentDocumentation } from '../../../api/workEnvironmentTypes';
import {
  integrityLines,
  logRows,
  participationFigures,
  participationTitle,
  partIntro,
  sensitiveItems,
  signatures,
} from '../inspectionReportView';
import { Figures, ReportSection, StatusTag } from './ReportSection';
import classes from '../InspectionReport.module.css';

const K = 'workEnvironmentInspection';

type Props = { report: WorkEnvironmentDocumentation };

/** Part 6: how many answered, proposed and highlighted, and whether the safety representative took part */
export const ParticipationPart = ({ report }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <ReportSection
      id="report-participation"
      part={6}
      title={participationTitle(report, t)}
      intro={partIntro(report, 'participationIntro', t)}
      newPage
    >
      <Figures items={participationFigures(report, t, i18n.language)} />
    </ReportSection>
  );
};

/** Part 7: the shielded areas and the routines, never with a level */
export const SensitivePart = ({ report }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <ReportSection
      id="report-sensitive"
      part={7}
      title={t(`${K}.parts.sensitive`)}
      intro={partIntro(report, 'sensitiveIntro', t)}
    >
      <ul className={classes.plainList}>
        {sensitiveItems(report, t, i18n.language).map((item) => (
          <li key={item.key} className={classes.row}>
            <span className={classes.rowText}>
              <span className="ds-label" data-size="sm">
                {item.title}
              </span>
              <span className={`ds-paragraph ${classes.subtle}`} data-size="sm">
                {item.text}
              </span>
            </span>
            <StatusTag label={item.status} color={item.color} />
          </li>
        ))}
      </ul>
      <Paragraph data-size="sm" className={classes.subtle}>
        {t(`${K}.parts.sensitiveNote`)}
      </Paragraph>
    </ReportSection>
  );
};

/** Part 8: why the numbers can be trusted, and the latest entries in the log */
export const ControlPart = ({ report }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <ReportSection id="report-control" part={8} title={t(`${K}.parts.control`)} intro={t(`${K}.parts.controlIntro`)}>
      <ul className={`ds-list ${classes.integrity}`} data-size="sm">
        {integrityLines(report, t).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div className={classes.tableWrap}>
        <Table data-size="sm" className={classes.table}>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell scope="col">{t(`${K}.log.id`)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(`${K}.log.at`)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(`${K}.log.role`)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(`${K}.log.event`)}</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {logRows(report, t, i18n.language).map((row) => (
              <Table.Row key={row.id}>
                <Table.Cell className={classes.nowrap}>{row.id}</Table.Cell>
                <Table.Cell className={classes.nowrap}>{row.at}</Table.Cell>
                <Table.Cell>{row.role}</Table.Cell>
                <Table.Cell>{row.event}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </ReportSection>
  );
};

/** Part 9: the team leader, and the signature of the safety representative */
export const SignaturePart = ({ report }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <ReportSection
      id="report-signature"
      part={9}
      title={t(`${K}.parts.signature`)}
      intro={partIntro(report, 'signatureIntro', t)}
    >
      <ul className={classes.signatures}>
        {signatures(report, t, i18n.language).map((signature) => (
          <li key={signature.key}>
            <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
              {signature.role}
            </span>
            <span className="ds-label">{signature.name}</span>
            <StatusTag label={signature.status} color={signature.color} />
            {signature.note && (
              <span className="ds-paragraph" data-size="xs">
                {signature.note}
              </span>
            )}
          </li>
        ))}
      </ul>
    </ReportSection>
  );
};
